// Receipt rendering + immediate thermal printing (80mm) utilities.
// The browser print dialog routes to the installed thermal printer.

import { receiptLabels } from './receiptLabels';
import api from './api';
import { eposPrintReceipt, eposPrintCard, eposOpenDrawer } from './eposPrint';
import { btPrintReceipt } from './btPrint';
import { toast } from 'sonner';

// Cache breve della configurazione stampante (per non interrogare il backend
// ad ogni stampa). Validità 30 secondi.
let _printerCache = { at: 0, cfg: null };
async function getPrinterConfig() {
  const now = Date.now();
  if (_printerCache.cfg && (now - _printerCache.at) < 30000) return _printerCache.cfg;
  try {
    const { data } = await api.get('/print/settings');
    _printerCache = { at: now, cfg: data?.printer || null };
  } catch (e) { _printerCache = { at: now, cfg: null }; }
  return _printerCache.cfg;
}
export function clearPrinterConfigCache() { _printerCache = { at: 0, cfg: null }; _cardLangCache = { at: 0, lang: null }; }

// Cache breve della lingua scontrini-tessera (fedeltà/buoni). Predefinita: spagnolo.
let _cardLangCache = { at: 0, lang: null };
export async function getCardLanguage() {
  const now = Date.now();
  if (_cardLangCache.lang && (now - _cardLangCache.at) < 30000) return _cardLangCache.lang;
  try {
    const { data } = await api.get('/print/settings');
    _cardLangCache = { at: now, lang: data?.card_language || 'es' };
  } catch (e) { _cardLangCache = { at: now, lang: 'es' }; }
  return _cardLangCache.lang;
}

const esc = (s) =>
  String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const eur = (n) => `\u20ac ${Number(n || 0).toFixed(2)}`;

const localeMap = { it: 'it-IT', es: 'es-ES', en: 'en-GB', fr: 'fr-FR', pt: 'pt-PT' };

const fmtDate = (iso, lang) => {
  if (iso === undefined || iso === null || iso === '') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso); // già formattata dal backend
  try {
    return d.toLocaleString(localeMap[(lang || 'it').toLowerCase()] || 'it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
};

// ===================== Stampante CAMERIERE (server-side) =====================
// La stampante cameriere è configurata UNA volta (dalla cassa/Admin) e salvata
// sul server per l'attività. Il backend include la config `waiter_printer` nella
// risposta della ricevuta quando l'utente è un cameriere: la stampa viene
// instradata su quella stampante di rete, senza uscire dalla principale.

/** Build a self-contained HTML document formatted for an 80mm (or 58mm) thermal roll. */
export function buildReceiptHtml(data) {
  const t = data.template || {};
  const r = data.restaurant || {};
  const o = data.order || {};
  const L = data.labels || receiptLabels(data.lang);
  const widthMm = Number(t.paper_width || 80);
  const contentMm = widthMm - 6; // small side margins

  const logo = t.logo_base64
    ? `<div class="logo"><img src="${t.logo_base64}" alt="logo"/></div>`
    : '';

  const headerExtra = (t.header_lines || [])
    .filter(Boolean)
    .map((l) => `<div class="muted">${esc(l)}</div>`)
    .join('');

  const fiscalRows = [];
  if (t.show_address && r.address) fiscalRows.push(esc(r.address));
  if (t.show_address && r.address_line2) fiscalRows.push(esc(r.address_line2));
  if (t.show_phone && r.phone) fiscalRows.push(`${L.phone_label}: ` + esc(r.phone));
  if (t.show_vat && r.vat) fiscalRows.push(`${L.vat_label} ` + esc(r.vat));
  const fiscal = fiscalRows.map((l) => `<div class="muted">${l}</div>`).join('');

  const lines = (data.lines || [])
    .map((it) => {
      const extras = (it.extras || [])
        .map((e) => `<div class="sub">+ ${esc(e.name)}${e.price ? ' (' + eur(e.price) + ')' : ''}</div>`)
        .join('');
      const variant = it.variant ? `<div class="sub">· ${esc(it.variant)}</div>` : '';
      const notes = it.notes ? `<div class="sub note">» ${esc(it.notes)}</div>` : '';
      return `
        <div class="row item">
          <div class="qty">${it.qty}x</div>
          <div class="desc">
            <div>${esc(it.name)}</div>
            ${variant}${extras}${notes}
          </div>
          <div class="amt">${eur(it.line_total)}</div>
        </div>`;
    })
    .join('');

  const discountRow = data.discount > 0
    ? `<div class="row tot"><span>${esc(L.discount)}${(data.discount_label || data.discount_code) ? ' (' + esc(data.discount_label || data.discount_code) + ')' : ''}</span><span>- ${eur(data.discount)}</span></div>`
    : '';

  const payment = data.payment || {};
  const paymentBlock = (payment.rows && payment.rows.length)
    ? `<div class="hr"></div>
       <div class="muted" style="font-weight:700;text-transform:uppercase;margin-bottom:2px">${esc(L.payment || 'Pagamento')}</div>
       ${payment.rows.map((row) => `<div class="row tot"><span>${esc(row.label)}</span><span>${eur(row.amount)}</span></div>`).join('')}`
    : '';

  const footer = (t.footer_lines || [])
    .filter(Boolean)
    .map((l) => `<div class="muted center">${esc(l)}</div>`)
    .join('');

  const qrBlock = data.qr_base64
    ? `<div class="hr"></div>
       <div class="qr center">
         <img src="${data.qr_base64}" alt="QR"/>
         <div class="muted center">${esc(L.qr_hint)}</div>
       </div>`
    : '';

  const nonFiscalBanner = data.is_fiscal
    ? ''
    : `<div class="banner">${esc(L.non_fiscal)}</div>`;

  return `<!doctype html>
<html lang="${esc(data.lang || 'it')}"><head><meta charset="utf-8"/>
<title>${esc(data.title || 'Scontrino')}</title>
<style>
  @page { size: ${widthMm}mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body {
    width: ${widthMm}mm;
    font-family: 'Courier New', ui-monospace, monospace;
    color: #000; font-size: 12px; line-height: 1.35;
  }
  .receipt { width: ${contentMm}mm; margin: 0 auto; padding: 4px 0 10px; }
  .center { text-align: center; }
  .muted { font-size: 11px; }
  .name { font-size: 15px; font-weight: 700; letter-spacing: .5px; }
  .title { font-size: 13px; font-weight: 700; text-transform: uppercase; margin-top: 4px; }
  .logo { text-align: center; margin-bottom: 4px; }
  .logo img { max-width: 70%; max-height: 90px; filter: grayscale(100%); }
  .hr { border-top: 1px dashed #000; margin: 6px 0; }
  .meta { font-size: 11px; }
  .meta .row { display: flex; justify-content: space-between; }
  .row { display: flex; gap: 4px; }
  .item { align-items: flex-start; margin: 2px 0; }
  .item .qty { width: 24px; font-weight: 700; }
  .item .desc { flex: 1; }
  .item .amt { text-align: right; min-width: 56px; font-variant-numeric: tabular-nums; }
  .sub { font-size: 10px; padding-left: 2px; color: #111; }
  .sub.note { font-style: italic; }
  .tot { justify-content: space-between; }
  .tot.grand { font-size: 15px; font-weight: 700; margin-top: 4px; }
  .vat { font-size: 10px; display: flex; justify-content: space-between; }
  .banner { text-align: center; font-weight: 700; font-size: 11px; border: 1px solid #000; padding: 2px; margin: 6px 0; }
  .footer { margin-top: 8px; }
  .qr { margin-top: 8px; }
  .qr img { width: 42%; max-width: 120px; image-rendering: pixelated; }
</style></head>
<body>
  <div class="receipt">
    ${logo}
    <div class="center name">${esc(r.name || '')}</div>
    <div class="center">${fiscal}${headerExtra}</div>
    <div class="center title">${esc(data.title || '')}</div>
    ${nonFiscalBanner}
    <div class="hr"></div>
    <div class="meta">
      <div class="row"><span>${esc(o.table_name || '')}</span><span>${o.covers ? esc(L.covers) + ': ' + o.covers : ''}</span></div>
      <div class="row"><span>${fmtDate(data.datetime_iso || data.datetime, data.lang)}</span><span>${o.waiter ? esc(o.waiter) : ''}</span></div>
    </div>
    <div class="hr"></div>
    ${lines}
    <div class="hr"></div>
    <div class="row tot"><span>${esc(L.subtotal)}</span><span>${eur(data.items_total)}</span></div>
    ${discountRow}
    <div class="row tot grand"><span>${esc(L.total)}</span><span>${eur(data.total)}</span></div>
    ${paymentBlock}
    <div class="hr"></div>
    <div class="vat"><span>${esc(L.taxable)}</span><span>${eur(data.imponibile)}</span></div>
    <div class="vat"><span>${esc(L.vat_included.replace('{rate}', Number(data.vat_rate).toFixed(0)))}</span><span>${eur(data.iva)}</span></div>
    <div class="footer center">${footer}</div>
    ${qrBlock}
  </div>
</body></html>`;
}

/** Render the receipt in a hidden iframe and immediately open the print dialog. */
function browserPrint(data) {
  const html = buildReceiptHtml(data);
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const cleanup = () => {
    setTimeout(() => {
      if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 1000);
  };

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  const triggerPrint = () => {
    if (iframe.dataset.printed === '1') return;
    iframe.dataset.printed = '1';
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      // no-op
    } finally {
      cleanup();
    }
  };

  // Wait for content (incl. logo image) to be ready, then print immediately.
  iframe.onload = () => setTimeout(triggerPrint, 250);
  // Fallback in case onload doesn't fire for the written document.
  setTimeout(() => {
    if (iframe.parentNode) triggerPrint();
  }, 900);
}

/**
 * Stampa una ricevuta. Se è configurata la stampa diretta dal dispositivo
 * (Epson ePOS-Print), invia direttamente alla stampante; in caso di errore o
 * se non configurata, ricade sulla stampa tramite finestra del browser.
 * Ritorna una Promise che si risolve con il metodo usato: 'epos' | 'browser'.
 */
export async function printReceipt(data) {
  try {
    const cfg = await getPrinterConfig();
    if (cfg && cfg.mode === 'bluetooth') {
      await btPrintReceipt(data, cfg.paper_width || 80);
      return 'bluetooth';
    }
    if (cfg && cfg.mode === 'device' && cfg.host) {
      await eposPrintReceipt(cfg, data);
      return 'epos';
    }
  } catch (e) {
    // Stampa diretta fallita: mostra l'errore reale (utile per diagnosi su
    // mobile dove non si vede la console) e ricade sulla stampa browser per
    // non perdere lo scontrino.
    const msg = e?.message || String(e);
    try { toast.error(`Stampa diretta fallita: ${msg}`); } catch { /* no-op */ }
    // eslint-disable-next-line no-console
    console.warn('Stampa diretta fallita, uso la stampa del browser:', msg);
    return 'browser';
  }
  // Nessuna config diretta valida: ripiego sulla stampa browser.
  try { toast.warning('Nessuna stampante diretta configurata: uso la stampa del browser.'); } catch { /* no-op */ }
  browserPrint(data);
  return 'browser';
}

/**
 * Stampa "intelligente": se la ricevuta contiene una configurazione
 * `waiter_printer` (fornita dal server quando l'utente è un cameriere e
 * l'attività ha una stampante cameriere attiva), stampa su QUELLA stampante
 * di rete; altrimenti usa la stampante principale.
 */
export async function printReceiptSmart(data) {
  const wp = data && data.waiter_printer;
  if (wp && wp.enabled && wp.host) {
    try {
      await eposPrintReceipt({ host: wp.host, protocol: wp.protocol || 'https', port: wp.port || 0, paper_width: wp.paper_width || 80 }, data);
      return 'epos-waiter';
    } catch (e) {
      const msg = e?.message || String(e);
      try { toast.error(`Stampa cameriere fallita: ${msg}`); } catch { /* no-op */ }
      // Non ricade sulla principale: ripiega sulla stampa del browser del dispositivo.
      browserPrint(data);
      return 'browser';
    }
  }
  return printReceipt(data);
}

/**
 * Request the bill for an order and print the non-fiscal summary (resoconto).
 * If the silent print agent is enabled, the backend already enqueued the
 * resoconto to the cashier printer; otherwise we fall back to browser printing.
 * Returns 'agent' or 'browser' to let callers show the right toast.
 */
export async function requestBillAndPrint(api, orderId) {
  // Il backend decide da solo (in base al ruolo cameriere + stampante cameriere
  // attiva) se NON stampare sulla principale; qui non serve passare flag.
  const { data } = await api.post(`/orders/${orderId}/request-bill`);
  const { data: receipt } = await api.get(`/orders/${orderId}/receipt`, { params: { type: 'resoconto' } });
  // Cameriere con stampante dedicata -> stampa lì; altrimenti principale/agente.
  if (receipt.waiter_printer && receipt.waiter_printer.enabled) return printReceiptSmart(receipt);
  if (data && data.print_agent_enabled) return 'agent';
  return printReceiptSmart(receipt);
}

/**
 * Stampa una TESSERA (fedeltà o gift card) come scontrino verticale.
 * `card` = { businessName, logo, title, lines[], valueLabel, valueText, code, qrUrl, footer }
 * `opts.qrDataUrl` = immagine QR (data URL) usata solo per il ripiego su stampa browser.
 * Usa la stampa diretta ePOS se configurata, altrimenti la stampa del browser.
 */
export async function printCard(card, opts = {}) {
  try {
    const cfg = await getPrinterConfig();
    if (cfg && cfg.mode === 'device' && cfg.host) {
      await eposPrintCard(cfg, card);
      return 'epos';
    }
    // Agente PC o Server Direct Print: accoda al canale esterno (niente anteprima browser).
    if (cfg && (cfg.mode === 'agent' || cfg.mode === 'sdp')) {
      await api.post('/print/card', {
        businessName: card.businessName || '',
        logo: card.logo || null,
        title: card.title || '',
        lines: card.lines || [],
        valueLabel: card.valueLabel || '',
        valueText: card.valueText != null ? String(card.valueText) : '',
        code: card.code || '',
        qrUrl: card.qrUrl || '',
        qrHint: card.qrHint || '',
        footer: card.footer || '',
      });
      return 'agent';
    }
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || String(e);
    try { toast.error(`Stampa tessera fallita: ${msg}`); } catch { /* no-op */ }
  }
  browserPrintCard(card, opts.qrDataUrl);
  return 'browser';
}

/**
 * Apertura MANUALE del cassetto, protetta dal PIN di sicurezza dell'attività.
 * Passa la funzione `runSensitive` (da useSecurityPin) per gestire il PIN.
 * - Agente/SDP: il backend accoda un job che apre il cassetto.
 * - ePOS diretta: l'impulso parte dal browser verso la stampante.
 */
export async function openCashDrawer(runSensitive) {
  const cfg = await getPrinterConfig();
  // Autorizza sul backend (richiede il PIN) e, per agente/SDP, accoda il job.
  const res = await runSensitive(
    (c) => api.post('/print/open-drawer', {}, c),
    { titleText: 'PIN per aprire il cassetto' },
  );
  if (res?.data?.handled) return 'agent';
  // ePOS diretta: invia l'impulso direttamente alla stampante vicino alla cassa.
  if (cfg && cfg.mode === 'device' && cfg.host) {
    await eposOpenDrawer(cfg);
    return 'epos';
  }
  throw new Error('Nessuna stampante configurata per aprire il cassetto (modalità: ' + (cfg?.mode || 'nessuna') + ')');
}


/** Ripiego: stampa la tessera dal browser (formato verticale 80mm). */
function browserPrintCard(card, qrDataUrl) {
  const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const lines = (card.lines || []).filter(Boolean).map((l) => `<div class="line">${esc(l)}</div>`).join('');
  const logo = card.logo ? `<img class="logo" src="${card.logo}" alt="logo" />` : (card.businessName ? `<div class="biz">${esc(card.businessName)}</div>` : '');
  const value = (card.valueText != null && card.valueText !== '')
    ? `<div class="vlabel">${esc(card.valueLabel || '')}</div><div class="value">${esc(card.valueText)}</div>` : '';
  const qr = qrDataUrl ? `<img class="qr" src="${qrDataUrl}" alt="qr" />` : '';
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Tessera</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  body { width: 80mm; margin: 0; padding: 6mm 4mm; font-family: 'Courier New', monospace; color:#000; text-align:center; }
  .logo { max-width: 60mm; max-height: 28mm; object-fit: contain; }
  .biz { font-size: 18px; font-weight: 800; }
  .title { font-weight:800; letter-spacing:1px; margin:8px 0; border-top:1px dashed #000; border-bottom:1px dashed #000; padding:4px 0; }
  .line { font-size: 14px; margin: 2px 0; }
  .vlabel { font-size: 12px; margin-top:8px; }
  .value { font-size: 30px; font-weight: 800; line-height:1.1; }
  .code { font-size: 16px; font-weight:700; letter-spacing:2px; margin-top:8px; }
  .qr { width: 40mm; height: 40mm; margin-top:8px; }
  .hint { font-size: 11px; margin-top:4px; }
  .footer { font-size: 11px; margin-top:8px; border-top:1px dashed #000; padding-top:4px; }
</style></head><body>
  ${logo}
  <div class="title">${esc(String(card.title || '').toUpperCase())}</div>
  ${lines}
  ${value}
  ${card.code ? `<div class="code">${esc(card.code)}</div>` : ''}
  ${qr}
  ${card.qrUrl ? `<div class="hint">${esc(card.qrHint || 'Inquadra il QR per i tuoi dati')}</div>` : ''}
  ${card.footer ? `<div class="footer">${esc(card.footer)}</div>` : ''}
  <script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>
</body></html>`;
  const w = window.open('', '_blank', 'width=380,height=640');
  if (!w) { try { toast.error('Abilita i popup per stampare la tessera'); } catch { /* */ } return; }
  w.document.open(); w.document.write(html); w.document.close();
}
